import { highlightedFeatureIdsAtom, highlightedLayerIdAtom } from '@/atoms/selection.atom';
import { Viewer } from '@/components';
import FullPageError from '@/components/FullPageError';
import FullPageLoader from '@/components/FullPageLoader';
import SkygisCaption from '@/components/SkygisCaption';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Pane } from '@/components/ui/pane';
import { ViewerSettingsDialog } from '@/components/ViewerSettingsDialog';
import { buildOrgUrl, isAppDomain } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { LayersQuery } from '@/graphql/graphql';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton, { iAction } from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import IconFull from '@/pages/Project/AttributeTable/icons/Full';
import IconFullViewer from '@/pages/Project/AttributeTable/icons/FullViewer';
import IconMapOneByOneHorizontal from '@/pages/Project/AttributeTable/icons/MapOneByOneHorizontal';
import IconMapOneByOneVertical from '@/pages/Project/AttributeTable/icons/MapOneByOneVertical';
import IconMapViewerHorizontal from '@/pages/Project/AttributeTable/icons/MapViewerHorizontal';
import IconMapViewerVertical from '@/pages/Project/AttributeTable/icons/MapViewerVertical';
import IconOneByOneHorizontal from '@/pages/Project/AttributeTable/icons/OneByOneHorizontal';
import IconOneByOneVertical from '@/pages/Project/AttributeTable/icons/OneByOneVertical';
import IconTwoByOneHorizontal from '@/pages/Project/AttributeTable/icons/TwoByOneHorizontal';
import IconTwoByOneVertical from '@/pages/Project/AttributeTable/icons/TwoByOneVertical';
import { Table } from '@/pages/Project/AttributeTable/Table';
import Layers from '@/pages/Project/Viewer/Layers';
import { layersQuery } from '@/queries/layer.query';
import { getSharedProjectQuery } from '@/queries/project.query';
import { CogFourSolid } from '@mynaui/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { Loader2, PanelRight } from 'lucide-react';
import { FunctionComponent, ReactNode, Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

interface iSharedPageProps {}

const MapView = lazy(() => import('@/components/MapView'));
const DEFAULT_LAYOUT = 'full-viewer';

interface LayoutSplit {
  direction: 'horizontal' | 'vertical';
  defaultSizes?: number[];
  children: LayoutNode[];
}

type LayoutNode = 'viewer' | 'table' | 'map' | LayoutSplit;

interface LayoutConfig {
  label: string;
  icon: ReactNode;
  layout: LayoutNode;
}

interface RenderContext {
  tableProps: Record<string, unknown>;
  projectId: string;
  hideMap: boolean;
}

function layoutContainsMap(node: LayoutNode): boolean {
  if (node === 'map') return true;
  if (typeof node === 'string') return false;
  return node.children.some(layoutContainsMap);
}

function SharedAttributeViewer({ projectId, hideMap }: { projectId: string; hideMap?: boolean }) {
  const { t } = useTranslation('common');

  return (
    <div className='h-full overflow-hidden relative flex-1'>
      <ErrorBoundary fallback={<div>{t('shared.error')}</div>}>
        <Suspense fallback={null}>
          {/* Keyed per project: see useStableMapOrigin. */}
          <Viewer key={projectId} projectId={projectId} hideLayers hideMap={hideMap} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function renderNode(node: LayoutNode, ctx: RenderContext, key: string): ReactNode {
  if (node === 'table') {
    return <Table {...(ctx.tableProps as any)} />;
  }
  if (node === 'viewer') {
    return <SharedAttributeViewer projectId={ctx.projectId} hideMap={ctx.hideMap} />;
  }
  if (node === 'map') {
    return (
      <Suspense fallback={null}>
        <MapView projectId={ctx.projectId} mode='standalone' />
      </Suspense>
    );
  }

  return (
    <Pane direction={node.direction} defaultSizes={node.defaultSizes} persistenceKey={key}>
      {node.children.map((child, i) => renderNode(child, ctx, `${key}-${i}`))}
    </Pane>
  );
}

const SharedPage: FunctionComponent<iSharedPageProps> = () => {
  const { t } = useTranslation(['common', 'project']);
  const { projectId } = useParams();
  if (!projectId) throw new Error('Token is required');

  const [params, setParams] = useSearchParams();
  const [isSettingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const isLayersEnabled = params.get('layers') === 'true';
  const [highlightedIds, setHighlightedIds] = useAtom(highlightedFeatureIdsAtom);
  const setHighlightedLayerId = useSetAtom(highlightedLayerIdAtom);

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['sharedProject', projectId],
    queryFn: () => execute(getSharedProjectQuery),
    retry: false,
  });

  const isPasswordRequired = error?.message === 'PASSWORD_REQUIRED';
  const isLinkExpired = error?.message === 'LINK_EXPIRED';

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setIsVerifying(true);
    setPasswordError('');

    sessionStorage.setItem('link-password', password);

    try {
      await queryClient.fetchQuery({
        queryKey: ['sharedProject', projectId],
        queryFn: () => execute(getSharedProjectQuery),
      });
    } catch (err: any) {
      sessionStorage.removeItem('link-password');
      if (err?.message === 'PASSWORD_REQUIRED') {
        setPasswordError(t('shared.invalidPassword'));
      } else if (err?.message === 'Network response was not ok') {
        setPasswordError(t('shared.invalidPassword'));
      } else {
        setPasswordError(t('shared.failedVerifyPassword'));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const project = data?.sharedProject;
  usePageMeta({ title: [project?.name] });

  const { data: layersData } = useQuery({
    queryKey: ['layers', project?.id],
    queryFn: () => execute(layersQuery, { projectId: project!.id }),
    enabled: !!project?.id,
  });

  const layers = (layersData?.layers ?? []) as LayersQuery['layers'];
  const activeLayerId = params.get('layerId') ?? layers.find((layer) => layer.type === 'VECTOR')?.id ?? '';
  const selectedFeatures = useMemo(() => Array.from(highlightedIds), [highlightedIds]);

  const _handleSelectLayer = (layerId: string) => {
    const next = new URLSearchParams(params);
    next.set('layerId', layerId);
    setParams(next, { replace: true });
  };

  const _handleSelect = (selection: string[]) => {
    setHighlightedIds(new Set(selection));
    setHighlightedLayerId(activeLayerId || null);
  };

  useEffect(() => {
    setHighlightedLayerId(activeLayerId || null);
  }, [activeLayerId, setHighlightedLayerId]);

  useEffect(() => {
    return () => {
      setHighlightedIds(new Set());
      setHighlightedLayerId(null);
    };
  }, [setHighlightedIds, setHighlightedLayerId]);

  useEffect(() => {
    if (project) {
      analytics.capture('Shared Project Viewed');
    }
  }, [project]);

  useEffect(() => {
    if (!project?.organization?.slug) return;
    if (!isAppDomain()) return;
    const path = `/shared/${projectId}${window.location.search}`;
    window.location.href = buildOrgUrl(project.organization.slug, path);
  }, [project?.organization?.slug, projectId]);

  const LAYOUTS: Record<string, LayoutConfig> = {
    'full-viewer': {
      label: t('attributes.viewerOnly', { ns: 'project' }),
      icon: <IconFullViewer />,
      layout: 'viewer',
    },
    '1-1-vertical-split': {
      label: t('attributes.viewerTable11', { ns: 'project' }),
      icon: <IconOneByOneVertical />,
      layout: {
        direction: 'vertical',
        defaultSizes: [50, 50],
        children: ['viewer', 'table'],
      },
    },
    '1-1-map-vertical-split': {
      label: t('attributes.mapTable11', { ns: 'project' }),
      icon: <IconMapOneByOneVertical />,
      layout: {
        direction: 'vertical',
        defaultSizes: [50, 50],
        children: ['map', 'table'],
      },
    },
    '1-1-map-viewer-vertical-split': {
      label: t('attributes.mapViewer11', { ns: 'project' }),
      icon: <IconMapViewerVertical />,
      layout: {
        direction: 'vertical',
        defaultSizes: [50, 50],
        children: ['map', 'viewer'],
      },
    },
    '2-1-vertical-split': {
      label: t('attributes.mapViewerTable211', { ns: 'project' }),
      icon: <IconTwoByOneVertical />,
      layout: {
        direction: 'vertical',
        defaultSizes: [50, 50],
        children: [
          {
            direction: 'horizontal',
            children: ['map', 'viewer'],
          },
          'table',
        ],
      },
    },
    '1-1-horizontal-split': {
      label: t('attributes.viewerTable11h', { ns: 'project' }),
      icon: <IconOneByOneHorizontal />,
      layout: {
        direction: 'vertical',
        children: [
          {
            direction: 'horizontal',
            children: ['viewer', 'table'],
          },
        ],
      },
    },
    '1-1-map-horizontal-split': {
      label: t('attributes.mapTable11h', { ns: 'project' }),
      icon: <IconMapOneByOneHorizontal />,
      layout: {
        direction: 'vertical',
        children: [
          {
            direction: 'horizontal',
            children: ['map', 'table'],
          },
        ],
      },
    },
    '1-1-map-viewer-horizontal-split': {
      label: t('attributes.mapViewer11h', { ns: 'project' }),
      icon: <IconMapViewerHorizontal />,
      layout: {
        direction: 'vertical',
        children: [
          {
            direction: 'horizontal',
            children: ['map', 'viewer'],
          },
        ],
      },
    },
    '2-1-horizontal-split': {
      label: t('attributes.mapViewerTable21h', { ns: 'project' }),
      icon: <IconTwoByOneHorizontal />,
      layout: {
        direction: 'horizontal',
        children: [
          {
            direction: 'vertical',
            children: ['viewer', 'map'],
          },
          'table',
        ],
      },
    },
    full: {
      label: t('attributes.fullTable', { ns: 'project' }),
      icon: <IconFull />,
      layout: 'table',
    },
  };

  const LAYOUT_KEYS = Object.keys(LAYOUTS);
  const layoutKey = LAYOUT_KEYS.includes(params.get('layout') ?? '') ? params.get('layout')! : DEFAULT_LAYOUT;
  const layout = LAYOUTS[layoutKey];

  const setLayout = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === DEFAULT_LAYOUT) {
      next.delete('layout');
    } else {
      next.set('layout', key);
    }
    setParams(next, { replace: true });
    if (key !== layoutKey) {
      analytics.capture('Project View Mode Changed', {
        projectId: project?.id,
        previousLayout: layoutKey,
        layout: key,
        source: 'shared',
      });
    }
  };

  const actions: (iAction | null)[] = [
    {
      icon: <CogFourSolid />,
      onClick: () => setSettingsDialogOpen(true),
      tooltip: t('shared.viewerSettings'),
      appearance: 'secondary',
    },
    {
      element: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className='size-7 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200/70 rounded-md text-slate-500 hover:text-slate-700 [&>svg]:size-4 [&>svg]:stroke-[2.25px] dark:text-stone-100 dark:hover:text-stone-100 dark:hover:bg-stone-800/50 dark:active:bg-stone-800/50'>
              {layout.icon}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {LAYOUT_KEYS.map((key) => {
              const config = LAYOUTS[key];
              return (
                <DropdownMenuItem key={key} onClick={() => setLayout(key)}>
                  {config.icon}
                  {config.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      tooltip: t('shared.layout'),
    },
    null,
    {
      icon: <PanelRight />,
      onClick: () => {
        const newParams = new URLSearchParams(window.location.search);
        newParams.set('layers', isLayersEnabled ? 'false' : 'true');
        setParams(newParams);
      },
      tooltip: t('shared.toggleLayers'),
      appearance: isLayersEnabled ? 'primary' : 'secondary',
    },
  ];

  if (isLoading) return <FullPageLoader />;

  if (isPasswordRequired) {
    return (
      <div className='h-full flex items-center justify-center'>
        <form onSubmit={handlePasswordSubmit} className='w-full max-w-sm space-y-4 p-6'>
          <div className='space-y-2 text-center'>
            <h1 className='text-xl font-semibold'>{t('shared.passwordRequired')}</h1>
            <p className='text-sm text-muted-foreground'>{t('shared.passwordProtected')}</p>
          </div>
          <div className='space-y-2'>
            <Input
              type='password'
              placeholder={t('shared.enterPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isVerifying}
              autoFocus
            />
            {passwordError && <p className='text-sm text-destructive'>{passwordError}</p>}
          </div>
          <Button type='submit' className='w-full' disabled={isVerifying || !password}>
            {isVerifying ? <Loader2 className='size-4 animate-spin' /> : t('shared.unlock')}
          </Button>
        </form>
      </div>
    );
  }

  if (isLinkExpired) {
    return <FullPageError title={t('shared.linkExpired')} subtitle={t('shared.linkExpiredDescription')} />;
  }

  if (error) {
    return (
      <FullPageError
        error={error}
        title={t('shared.failedLoadProject')}
        subtitle={t('shared.failedLoadProjectDescription')}
      />
    );
  }
  return (
    <div className='h-full flex flex-col'>
      <PageSkeleton
        hideSidebar
        branding={<SkygisCaption className='h-6' />}
        steps={[{ label: project?.name || t('shared.loading'), href: `/shared/${projectId}` }]}
        actions={actions}
      >
        <div className='flex-1 flex overflow-hidden'>
          <div className='flex-1 min-w-0 overflow-hidden flex'>
            {project?.id
              ? renderNode(
                  layout.layout,
                  {
                    tableProps: {
                      projectId: project.id,
                      layerId: activeLayerId,
                      selection: selectedFeatures,
                      onSelect: _handleSelect,
                      layers,
                    },
                    projectId: project.id,
                    hideMap: layoutContainsMap(layout.layout),
                  },
                  `shared-pane-${layoutKey}`,
                )
              : t('shared.loading')}
          </div>
          <ErrorBoundary
            fallback={<div className='flex flex-col items-center justify-center px-4'>{t('shared.error')}</div>}
          >
            <Layers projectId={project?.id!} selectedLayerId={activeLayerId} onSelectLayer={_handleSelectLayer} />
          </ErrorBoundary>
        </div>
      </PageSkeleton>
      {isSettingsDialogOpen && (
        <ViewerSettingsDialog open={isSettingsDialogOpen} onOpenChange={setSettingsDialogOpen} />
      )}
    </div>
  );
};

export default SharedPage;
